import React, { useEffect, useState } from "react";
import {
  useApiUrl,
  useCustom,
  useCustomMutation,
  useTranslate,
} from "@refinedev/core";
import { Button, Divider, Switch, Typography, message, theme } from "antd";

const { Title, Text } = Typography;

/**
 * Per-user preferences backed by `users/my-settings`.
 *
 * These settings previously had no home: `autoStartOrder` was reachable only
 * through a modal on the order detail page, and the Telegram chat id was set
 * from the notifications tab. This gives the endpoint a single visible owner;
 * the order-detail modal stays as an in-context shortcut.
 */
export const PreferencesTab: React.FC = () => {
  const _t = useTranslate();
  const apiUrl = useApiUrl();
  const { token } = theme.useToken();

  const { data, isLoading, refetch } = useCustom<Record<string, unknown>>({
    url: `${apiUrl}/users/my-settings`,
    method: "get",
  });
  const { mutate: save, isLoading: saving } = useCustomMutation();

  const [autoStartOrder, setAutoStartOrder] = useState(false);

  useEffect(() => {
    const settings = data?.data as Record<string, unknown> | undefined;
    setAutoStartOrder(settings?.autoStartOrder === true);
  }, [data]);

  const handleSave = () => {
    save(
      {
        url: `${apiUrl}/users/my-settings`,
        method: "patch",
        values: { autoStartOrder },
      },
      {
        onSuccess: () => {
          refetch();
          message.success(_t("settings.preferencesTab.saveSuccess"));
        },
        onError: () => message.error(_t("settings.preferencesTab.saveError")),
      },
    );
  };

  return (
    <div>
      <Title level={5} className="!mb-1">
        {_t("settings.preferencesTab.title")}
      </Title>
      <Text type="secondary" className="text-sm block mb-6">
        {_t("settings.preferencesTab.subtitle")}
      </Text>

      <Divider orientation="left" plain>
        {_t("settings.preferencesTab.ordersSection")}
      </Divider>

      <div className="flex items-start justify-between gap-4 py-2">
        <div className="flex-1 min-w-0">
          <div className="font-medium" style={{ color: token.colorText }}>
            {_t("orders.settings.autoStartOrder")}
          </div>
        </div>
        <Switch
          checked={autoStartOrder}
          onChange={setAutoStartOrder}
          loading={isLoading}
          className="!min-w-[44px] shrink-0"
        />
      </div>

      <Button
        type="primary"
        onClick={handleSave}
        loading={saving}
        className="mt-6"
      >
        {_t("buttons.save")}
      </Button>
    </div>
  );
};
